import { isTauri } from "@/lib/is-tauri";

/**
 * Write text to the clipboard; false when it could not. The desktop app
 * writes through the native clipboard, which needs no user gesture, so a
 * copy that first waits for content still lands. The web uses the browser
 * clipboard, then the legacy selection copy.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (isTauri()) {
    try {
      const { writeText } =
        await import("@tauri-apps/plugin-clipboard-manager");
      await writeText(text);
      return true;
    } catch {
      return false;
    }
  }

  if (navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to the legacy path below.
    }
  }

  try {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.setAttribute("readonly", "");
    textArea.style.position = "fixed";
    textArea.style.top = "0";
    textArea.style.left = "-9999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const copied = document.execCommand("copy");
    document.body.removeChild(textArea);
    return copied;
  } catch {
    return false;
  }
}

/**
 * The clipboard's text, read where `copyToClipboard` writes: the native
 * clipboard in the desktop app, the browser's on the web. `null` where the
 * browser offers no read; a refused read rejects.
 */
export async function readClipboardText(): Promise<string | null> {
  if (isTauri()) {
    const { readText } = await import("@tauri-apps/plugin-clipboard-manager");
    return readText();
  }
  if (!navigator.clipboard?.readText) return null;
  return navigator.clipboard.readText();
}
